import React, {useEffect} from "react"
import {Button, Modal} from "react-bootstrap"
import {defineMessages, FormattedMessage, injectIntl} from "react-intl"
import {
    getOptionalItemSelection,
    getItemQuantities,
    getMultiChoiceSelection,
    getSubtotalAmount,
    getTotalAmount
} from "../../state/selection/selectionSelectors"
import {
    editItemQuantity, selectMultipleChoice, toggleOptionalItem, updateTotals,
} from "../../state/selection/selectionActions"
import {connect, ConnectedProps} from "react-redux"
import ProposalService from "../../state/proposal/proposalService"
import Proposal, { ProposalItemDto } from "../../state/proposal/Proposal"
import IntlProps from "../../state/helpers/IntlProps"
import ActiveProductSelectionComponent from "../components/ActiveItemSelectionComponent"
import OptionalItemSelectionComponent from "../components/OptionalItemSelectionComponent"
import MultiChoiceItemSelectionComponent from "../components/MultiChoiceItemSelectionComponent"
import SimpleDisplayTotalsComponent from "../components/SimpleDisplayTotalsComponent"
import BulkCreateWarningComponent from "../components/BulkCreateWarningComponent"

const messages = defineMessages({
    modal_title: {
        id: "price_list.bulk.configure_price_list_modal.title",
        defaultMessage: "Configure Items",
    },
    modal_close_btn: {
        id: "price_list.bulk_create_modal.close_btn",
        defaultMessage: "Close",
    },
    modal_next_btn: {
        id: "price_list.bulk_create_modal.next_btn",
        defaultMessage: "Next",
    },
})

const itemType = {
    active: "active",
    optional: "option-for-customer",
    multiChoice: {
        option: "multiple-choice",
        selected: "choice-selected"
    },
}

type OwnProps = {
    onHide: () => void
    show: boolean
    onNext: () => void
    onPrev: () => void
    proposal: Proposal,
}

const ConfigureItemSelectionModal = (props: Props & IntlProps) => {
    const { proposal } = props

    useEffect(() => {
        if(props.selectedChoices.length === 0){
            //add each selected item from each multi choice group to selection listing
            getMultiChoiceItems().forEach(items => {
                items.forEach(p => {
                    if (p.type == itemType.multiChoice.selected) {
                        props.selectMultipleChoice(p.id, items.map(p => p.id))
                    }
                })
            })
        }

        if (props.itemQuantities.length === 0){
            getMeasureEditableProducts().forEach(p => {
                props.editItemQuantity(p.id, p.measure)
            })
        }
    }, [])

    useEffect(() => {
        void updateTotals()
    }, [props.chosenOptions, props.itemQuantities, props.selectedChoices])

    const updateTotals = async () => {
        const res = await ProposalService.calculateTotal(
            proposal.attributes.hash,
            props.chosenOptions,
            // @ts-ignore
            props.itemQuantities,
            props.selectedChoices,
        )
        if (res instanceof Proposal) {
            props.updateTotals(res.getSubtotalAmount(), res.getTotalAmount())
        }
    }

    const getActiveItems = (): ProposalItemDto[] => {
        return proposal.getItems().filter(p => {
            return p.type == itemType.active
        })
    }
    const getOptionalItems = (): ProposalItemDto[] => {
        return proposal.getItems().filter(p => {
            return p.type == itemType.optional
        })
    }

    const getMultiChoiceItems = (): ProposalItemDto[][] => {
        const multiChoices = proposal.getItems().filter(p => {
            return p.type == itemType.multiChoice.option || p.type == itemType.multiChoice.selected
        })
        return Object.values(multiChoices.reduce((items, item) => {
            const id = item.groupId || "default";
            (items[id] = items[id] || []).push(item)
            return items
        }, {}))
    }

    const getMeasureEditableProducts = (): ProposalItemDto[] => {
        return proposal.getItems().filter(p => {
            return p.isEditableQuantity
        })
    }

    return (
        <Modal
            {...props}
            scrollable={true}
            size={"lg"}
            backdrop="static">
            <Modal.Header closeButton>
                <Modal.Title>
                    <FormattedMessage{...messages.modal_title} />
                </Modal.Title>
            </Modal.Header>

            <Modal.Body className="p-4">
                <div className="row pl-2 pr-2">
                    <BulkCreateWarningComponent />
                </div>

                {getOptionalItems().length > 0 ?
                    <OptionalItemSelectionComponent
                        optionalItems={getOptionalItems()}
                    /> : ""
                }

                {getMultiChoiceItems().length > 0 ?
                    <MultiChoiceItemSelectionComponent
                        multiChoiceItems={getMultiChoiceItems()}
                    /> : ""
                }

                {getActiveItems().length > 0 ?
                    <ActiveProductSelectionComponent
                        activeItems={getActiveItems()}
                    /> : ""
                }

                <SimpleDisplayTotalsComponent
                    subtotal={props.subtotal}
                    total={props.total}
                    currency={proposal.getCurrency()}
                />
            </Modal.Body>

            <Modal.Footer>
                <Button
                    onClick={props.onPrev}
                    variant="secondary" 
                >
                    <FormattedMessage
                        id="price_list.bulk_create_modal.close_btn"
                        defaultMessage="Close"
                    />
                </Button>

                <Button
                    onClick={props.onNext}
                    variant="primary">
                    <FormattedMessage {...messages.modal_next_btn} />
                </Button>
            </Modal.Footer>
        </Modal>
    )
}

ConfigureItemSelectionModal.defaultProps = {
    onHide: () => { return },
    show: false,
    onNext: () => { return },
    productList: [],
}

const mapState = (state, props: OwnProps) => {
    return {
        ...props,
        itemQuantities: getItemQuantities(state),
        selectedChoices: getMultiChoiceSelection(state),
        chosenOptions: getOptionalItemSelection(state),
        subtotal: getSubtotalAmount(state),
        total: getTotalAmount(state),
    }
}
const mapDispatch = {
    toggleOptionalProduct: toggleOptionalItem,
    editItemQuantity,
    selectMultipleChoice,
    updateTotals,
}
const connector = connect(mapState, mapDispatch)
type Props = ConnectedProps<typeof connector>
export default connector(injectIntl(ConfigureItemSelectionModal))


// WEBPACK FOOTER //
// ./src/price-list/modal/ConfigureItemSelectionModal.tsx